/* eslint-disable jsx-a11y/anchor-is-valid */

import { getCurrentThread } from '@/store/reducers/UIState';
import { isCorpusIdSearchable } from '@/api/ChatApi';
import { openReaderWidget } from '@/store/actionCreators/WidgetActions';
import { PaperDetails } from '@/models/PaperDetails';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { ErrorBoundary } from 'react-error-boundary';
import { FC, useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';

interface PaperCardProps {
  corpusId: number;
}

export const PaperCard: FC<PaperCardProps> = (props: PaperCardProps) => {
  const { corpusId } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [paper, setPaper] = useState<PaperDetails | null>(null);
  const [isSearchable, setIsSearchable] = useState<boolean>(false);

  useEffect(() => {
    isCorpusIdSearchable(corpusId).then((isSearchable) => {
      setIsSearchable(isSearchable);
    });
  }, [corpusId]);

  useEffect(() => {
    setLoading(true);
    const urlbase = 'https://mage.allen.ai';
    const fields = [
      'title',
      'authors',
      'venue',
      'year',
      'tldr',
      'citationCount',
    ].join(',');
    fetch(`${urlbase}/details/${corpusId}?fields=${fields}`)
      .then((response) => {
        if (response.status === 200) {
          return response.json() as any;
        }
        return null;
      })
      .then((data) => {
        if (!data || data?.message === 'Forbidden') {
          setPaper(null);
        } else {
          setPaper(data as PaperDetails);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [corpusId]);

  const currentThread = useAppSelector(getCurrentThread);
  const dispatch = useAppDispatch();

  const handleOpenReader = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      if (!currentThread) {
        return;
      }
      dispatch(
        openReaderWidget({ currentThreadId: currentThread.id, corpusId }),
      );
    },
    [corpusId, currentThread, dispatch],
  );

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }
  if (!paper) {
    return <div style={{ padding: '20px' }}>No data.</div>;
  }

  return (
    <ErrorBoundary fallback={<div>Something went wrong</div>}>
      <a
        href={`https://api.semanticscholar.org/CorpusId:${corpusId}`}
        target="_blank"
        rel="noreferrer noopener"
      >
        <Typography sx={{ mb: 1 }} variant="subtitle2" component="div">
          {paper.title}
        </Typography>
      </a>
      {isSearchable && (
        <div>
          <a href="#" onClick={handleOpenReader}>
            Read with Nora
          </a>
        </div>
      )}
      <Typography sx={{ mb: 1.5 }} variant="subtitle1" color="text.secondary">
        {(paper.authors ?? []).map((author) => author.name).join(', ')}
      </Typography>
      {paper.tldr?.text && (
        <Typography sx={{ mb: 1.5 }} variant="body2">
          TLDR: {paper.tldr.text}
        </Typography>
      )}
      <Typography sx={{ mb: 1.5 }} variant="subtitle1" color="text.secondary">
        {paper.venue}. {paper.year}.
      </Typography>
    </ErrorBoundary>
  );
};

export default PaperCard;
